import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { adminService } from '../../../services/adminService';
import { userService } from '../../../services/userService';
import { EditUserModal } from '../../organisms/EditUserModal';

const Container = styled.div`
  max-width: 1400px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin: 0 0 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const Thead = styled.thead`
  background-color: #f8f9fa;
`;

const Th = styled.th`
  padding: 12px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:hover {
    background-color: #f8f9fa;
  }

  border-bottom: 1px solid #dee2e6;
`;

const Td = styled.td`
  padding: 14px 12px;
  font-size: 14px;
  color: #495057;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.$type) {
      case 'admin': return '#e7f3ff';
      case 'teacher': return '#fff3e7';
      case 'mom': return '#ffe7f3';
      case 'inactive': return '#f0f0f0';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'admin': return '#007bff';
      case 'teacher': return '#ff8c00';
      case 'mom': return '#e91e63';
      case 'inactive': return '#666';
      default: return '#666';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #0056b3;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 16px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.$color || '#007bff'};
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #333;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background-color: ${props => props.$active ? '#007bff' : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.$active ? '#0056b3' : '#f8f9fa'};
  }
`;

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('teacher'); // 'teacher' or 'mom'
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await adminService.getAllUsers();
      setUsers(data);
      // 치료사 목록 필터링
      setTeachers(data.filter(u => u.type === 'teacher'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  const handleSaveUser = async (userId, updatedData) => {
    try {
      await userService.updateUserDocument(userId, updatedData);
      alert('회원 정보가 수정되었습니다.');
      await fetchUsers(); // 데이터 새로고침
    } catch (error) {
      throw error;
    }
  };

  const getStats = () => {
    const teachers = users.filter(u => u.type === 'teacher').length;
    const moms = users.filter(u => u.type === 'mom').length;

    return { teachers, moms };
  };

  const getFilteredUsers = () => {
    return users.filter(u => u.type === activeTab);
  };

  const getTeacherNames = (teacherIds) => {
    if (!teacherIds || teacherIds.length === 0) return '미배정';
    const names = teacherIds
      .map(teacherId => {
        const teacher = teachers.find(t => t.uid === teacherId || t.id === teacherId);
        return teacher ? teacher.name : null;
      })
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : '미배정';
  };

  const stats = getStats();
  const filteredUsers = getFilteredUsers();

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('ko-KR');
    } catch {
      return '-';
    }
  };

  return (
    <Container>
      <PageTitle>회원관리</PageTitle>

      <Stats>
        <StatCard $color="#ff8c00">
          <StatLabel>치료사</StatLabel>
          <StatValue>{stats.teachers}</StatValue>
        </StatCard>
        <StatCard $color="#e91e63">
          <StatLabel>학부모</StatLabel>
          <StatValue>{stats.moms}</StatValue>
        </StatCard>
      </Stats>

      <TabContainer>
        <Tab $active={activeTab === 'teacher'} onClick={() => setActiveTab('teacher')}>
          치료사
        </Tab>
        <Tab $active={activeTab === 'mom'} onClick={() => setActiveTab('mom')}>
          학부모
        </Tab>
      </TabContainer>

      <Card>
        {loading && <LoadingMessage>회원 정보를 불러오는 중...</LoadingMessage>}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {!loading && !error && filteredUsers.length === 0 && (
          <EmptyMessage>등록된 {activeTab === 'teacher' ? '치료사' : '학부모'}가 없습니다.</EmptyMessage>
        )}

        {!loading && !error && filteredUsers.length > 0 && (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  {activeTab === 'teacher' ? (
                    <>
                      <Th>이름</Th>
                      <Th>이메일</Th>
                      <Th>전화번호</Th>
                      <Th>가입일</Th>
                      <Th>관리</Th>
                    </>
                  ) : (
                    <>
                      <Th>학부모 이름</Th>
                      <Th>학생 이름</Th>
                      <Th>전화번호</Th>
                      <Th>출생년도</Th>
                      <Th>성별</Th>
                      <Th>배정된 튜터</Th>
                      <Th>활성회원</Th>
                      <Th>관리</Th>
                    </>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {filteredUsers.map((user) => (
                  <Tr key={user.id}>
                    {activeTab === 'teacher' ? (
                      <>
                        <Td>{user.name}</Td>
                        <Td>{user.email}</Td>
                        <Td>{user.phone}</Td>
                        <Td>{formatDate(user.createdAt)}</Td>
                        <Td>
                          <ActionButton onClick={() => handleEditUser(user)}>
                            수정
                          </ActionButton>
                        </Td>
                      </>
                    ) : (
                      <>
                        <Td>{user.name}</Td>
                        <Td>{user.studentName || '-'}</Td>
                        <Td>{user.phone}</Td>
                        <Td>{user.birthYear || '-'}</Td>
                        <Td>
                          <Badge $type={user.gender === 'male' ? 'teacher' : 'mom'}>
                            {user.gender === 'male' ? '남아' : user.gender === 'female' ? '여아' : '-'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge $type={user.teacher_ids?.length > 0 ? 'teacher' : 'inactive'}>
                            {getTeacherNames(user.teacher_ids)}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge $type={user.isActive ? 'admin' : 'inactive'}>
                            {user.isActive ? '활성' : '비활성'}
                          </Badge>
                        </Td>
                        <Td>
                          <ActionButton onClick={() => handleEditUser(user)}>
                            수정
                          </ActionButton>
                        </Td>
                      </>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        user={editingUser}
        onSave={handleSaveUser}
        teachers={teachers}
      />
    </Container>
  );
};

export default UserManagementPage;
